import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { useNavigate } from "react-router-dom";
import { UNIVERSE } from "@/lib/universe";
import { ArrowLeft, X, Radio, Map as MapIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import UniverseMapSvg from "@/components/UniverseMapSvg";

// ─────────────────────────────────────────────────────────────
// helpers
const loader = new THREE.TextureLoader();
const texCache = new Map();
function loadTex(url) {
  if (!url) return null;
  if (texCache.has(url)) return texCache.get(url);
  const t = loader.load(url);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  texCache.set(url, t);
  return t;
}

function disposeGroup(g) {
  g.traverse((o) => {
    if (o.geometry) o.geometry.dispose();
    if (o.material) {
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((m) => {
        Object.keys(m).forEach((k) => {
          if (m[k] && m[k].isTexture) m[k].dispose();
        });
        m.dispose();
      });
    }
  });
  while (g.children.length) g.remove(g.children[0]);
}

// ─────────────────────────────────────────────────────────────
// star field background
function makeStars(count = 6000, radius = 900) {
  const g = new THREE.BufferGeometry();
  const pos = new Float32Array(count * 3);
  const col = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const r = radius * (0.7 + Math.random() * 0.3);
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    pos[i * 3 + 2] = r * Math.cos(phi);
    const c = Math.random();
    if (c > 0.94) { col[i * 3] = 0.7; col[i * 3 + 1] = 0.85; col[i * 3 + 2] = 1.0; }
    else if (c > 0.88) { col[i * 3] = 1.0; col[i * 3 + 1] = 0.7; col[i * 3 + 2] = 0.4; }
    else { col[i * 3] = 1; col[i * 3 + 1] = 1; col[i * 3 + 2] = 1; }
  }
  g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  const m = new THREE.PointsMaterial({ size: 1.6, vertexColors: true, transparent: true, opacity: 0.9, depthWrite: false, sizeAttenuation: true });
  return new THREE.Points(g, m);
}

// ─────────────────────────────────────────────────────────────
export default function Explorer() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const mountRef = useRef(null);
  const stateRef = useRef({});
  // Read initial view from URL query (?g=MW&s=sol) so we can deep-link
  const initialView = (() => {
    if (typeof window === "undefined") return { level: "universe", galaxyId: null, systemId: null, planetIdx: null };
    const q = new URLSearchParams(window.location.search);
    const g = q.get("g");
    const s = q.get("s");
    if (g && s) return { level: "system", galaxyId: g, systemId: s, planetIdx: null };
    if (g) return { level: "galaxy", galaxyId: g, systemId: null, planetIdx: null };
    return { level: "universe", galaxyId: null, systemId: null, planetIdx: null };
  })();
  const [view, setView] = useState(initialView);
  const [hovered, setHovered] = useState(null); // { name, subtitle, x, y }
  const [selected, setSelected] = useState(null);
  const [ready, setReady] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [following, setFollowing] = useState(null); // { name } — displayed as chip while camera locks onto a moving body
  const [fastMode, setFastMode] = useState(false); // realistic vs time-lapse

  // Init Three.js
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x02060d);
    scene.fog = new THREE.FogExp2(0x02060d, 0.0008);
    // Preload two starfield backdrops:
    //   universe view  → dramatic Milky Way panorama shown as flat 2D backdrop (fits within viewport, no wraparound)
    //   galaxy/system  → equirectangular skybox that surrounds camera
    const bgLoader = new THREE.TextureLoader();
    const bgTextures = { universe: null, deep: null };
    const applyBg = () => {
      const level = stateRef.current?.viewLevel || "universe";
      const t = level === "universe" ? bgTextures.universe : bgTextures.deep;
      if (t) scene.background = t;
    };
    bgLoader.load("/textures/space-bg-universe.jpg", (tex) => {
      // Default mapping (UVMapping) → three.js draws it as a full-viewport 2D backdrop (cover-fit)
      tex.colorSpace = THREE.SRGBColorSpace;
      bgTextures.universe = tex;
      if ("backgroundIntensity" in scene) scene.backgroundIntensity = 0.9;
      applyBg();
    });
    bgLoader.load("/textures/space-bg.webp", (tex) => {
      tex.mapping = THREE.EquirectangularReflectionMapping;
      tex.colorSpace = THREE.SRGBColorSpace;
      bgTextures.deep = tex;
      applyBg();
    });
    scene.fog = new THREE.FogExp2(0x02060d, 0.00035);

    const camera = new THREE.PerspectiveCamera(55, mount.clientWidth / mount.clientHeight, 0.1, 3000);
    camera.position.set(0, 25, 90);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;
    mount.appendChild(renderer.domElement);

    // lighting — brighter for good visibility of planets/galaxies
    const ambient = new THREE.AmbientLight(0x8899bb, 0.85);
    const dir1 = new THREE.DirectionalLight(0xaad4ff, 0.7);
    dir1.position.set(50, 40, 30);
    const dir2 = new THREE.DirectionalLight(0xffddaa, 0.35);
    dir2.position.set(-60, -20, -40);
    const hemi = new THREE.HemisphereLight(0x88bbff, 0x223344, 0.45);
    scene.add(ambient, dir1, dir2, hemi);
    const sunLight = new THREE.PointLight(0xffffff, 3.5, 800, 1.0);
    scene.add(sunLight);

    // scene groups
    const worldGroup = new THREE.Group();
    scene.add(worldGroup);

    // orbit controls (custom, no dep) — mutate stateRef.current so animate() reads updates
    let rotX = 0.3, rotY = 0, dist = 90;
    let dragging = false, lastX = 0, lastY = 0;

    const dom = renderer.domElement;
    dom.style.cursor = "grab";
    dom.style.touchAction = "none";

    const onDown = (e) => {
      dragging = true;
      dom.style.cursor = "grabbing";
      const t = e.touches ? e.touches[0] : e;
      lastX = t.clientX; lastY = t.clientY;
    };
    const onUp = () => { dragging = false; dom.style.cursor = "grab"; };
    const onMove = (e) => {
      const t = e.touches ? e.touches[0] : e;
      const s = stateRef.current;
      if (dragging && s) {
        const dx = t.clientX - lastX, dy = t.clientY - lastY;
        s.targetRotY -= dx * 0.006;
        s.targetRotX = Math.max(-1.35, Math.min(1.35, s.targetRotX - dy * 0.006));
        lastX = t.clientX; lastY = t.clientY;
      }
      // hover raycast
      const rect = dom.getBoundingClientRect();
      pointer.x = ((t.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((t.clientY - rect.top) / rect.height) * 2 + 1;
    };
    const onWheel = (e) => {
      e.preventDefault();
      const s = stateRef.current;
      if (!s) return;
      s.targetDist = Math.max(4, Math.min(600, s.targetDist * (1 + e.deltaY * 0.0015)));
    };
    dom.addEventListener("mousedown", onDown);
    dom.addEventListener("mouseup", onUp);
    dom.addEventListener("mouseleave", onUp);
    dom.addEventListener("mousemove", onMove);
    dom.addEventListener("wheel", onWheel, { passive: false });
    dom.addEventListener("touchstart", onDown, { passive: true });
    dom.addEventListener("touchend", onUp);
    dom.addEventListener("touchmove", onMove, { passive: true });

    // raycaster
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    const onClick = (e) => {
      const rect = dom.getBoundingClientRect();
      const cx = (e.clientX ?? (e.changedTouches && e.changedTouches[0].clientX));
      const cy = (e.clientY ?? (e.changedTouches && e.changedTouches[0].clientY));
      if (cx == null) return;
      pointer.x = ((cx - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((cy - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(worldGroup.children, true);
      for (const h of hits) {
        let n = h.object;
        while (n && !n.userData?.clickable) n = n.parent;
        if (n && n.userData.clickable) {
          n.userData.onClick?.();
          break;
        }
      }
    };
    dom.addEventListener("click", onClick);

    // Double-click → camera follows the clicked orbiting body
    const onDblClick = (e) => {
      const rect = dom.getBoundingClientRect();
      const cx = (e.clientX ?? (e.changedTouches && e.changedTouches[0].clientX));
      const cy = (e.clientY ?? (e.changedTouches && e.changedTouches[0].clientY));
      if (cx == null) return;
      pointer.x = ((cx - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((cy - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(worldGroup.children, true);
      for (const h of hits) {
        let n = h.object;
        while (n && !n.userData?.clickable) n = n.parent;
        if (n && n.userData.clickable) {
          const s = stateRef.current;
          // Toggle off if already following the same object
          if (s.followMesh === n) {
            s.followMesh = null;
            setFollowing(null);
          } else {
            s.followMesh = n;
            // zoom in — larger body ⇒ farther, small body ⇒ closer
            const geoParams = n.geometry?.parameters;
            const r = geoParams?.radius ?? 1;
            s.targetDist = Math.max(3.5, r * 6);
            setFollowing({ name: n.userData.title || "Alvo" });
          }
          break;
        }
      }
    };
    dom.addEventListener("dblclick", onDblClick);

    stateRef.current = {
      scene, camera, renderer, worldGroup, sunLight, ambient,
      bgTextures,
      viewLevel: initialView.level,
      targetRotX: 0.3, targetRotY: 0, targetDist: 90,
      target: new THREE.Vector3(0, 0, 0),
      camTarget: new THREE.Vector3(0, 0, 0),
      followMesh: null,
      speedMult: 1,   // 1 = realistic (slow), higher = time-lapse
      raycaster, pointer, dom,
    };

    // animation loop
    let running = true;
    const clock = new THREE.Clock();
    const animate = () => {
      if (!running) return;
      const t = clock.getElapsedTime();
      const s = stateRef.current;
      // camera lerp
      rotX += (s.targetRotX - rotX) * 0.09;
      rotY += (s.targetRotY - rotY) * 0.09;
      dist += (s.targetDist - dist) * 0.09;
      // If following a body, update s.target every frame to its world position
      if (s.followMesh) {
        // Ensure the mesh is still in the scene (level changes may dispose it)
        if (!s.followMesh.parent) {
          s.followMesh = null;
          setFollowing(null);
        } else {
          s.followMesh.getWorldPosition(s.target);
        }
      }
      s.camTarget.lerp(s.target, s.followMesh ? 0.18 : 0.08);
      const cx = s.camTarget.x + dist * Math.cos(rotX) * Math.sin(rotY);
      const cy = s.camTarget.y + dist * Math.sin(rotX);
      const cz = s.camTarget.z + dist * Math.cos(rotX) * Math.cos(rotY);
      camera.position.set(cx, cy, cz);
      camera.lookAt(s.camTarget);

      // update orbits — slower (period * 40 = way slower than before)
      const _tmpPivot = new THREE.Vector3();
      worldGroup.traverse((o) => {
        if (o.userData.orbit) {
          const { period, angle0, incX, incZ, parentMesh } = o.userData.orbit;
          // Moons: pivot follows their parent mesh's world position each frame
          let pivot;
          if (parentMesh) {
            parentMesh.getWorldPosition(_tmpPivot);
            pivot = _tmpPivot;
          } else {
            pivot = o.userData.orbit.pivot;
          }
          const a = angle0 + t * (2 * Math.PI * s.speedMult / (period * 120));
          const r = o.userData.orbit.radius;
          const localX = Math.cos(a) * r;
          const localZ = Math.sin(a) * r;
          // apply inclination
          const ix = incX || 0;
          const iz = incZ || 0;
          const y = localZ * Math.sin(ix) + localX * Math.sin(iz);
          const x = localX * Math.cos(iz);
          const z = localZ * Math.cos(ix);
          o.position.x = pivot.x + x;
          o.position.y = pivot.y + y;
          o.position.z = pivot.z + z;
        }
        if (o.userData.spin) o.rotation.y += o.userData.spin;
      });

      // hover raycast
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(worldGroup.children, true);
      let hoveredNode = null;
      for (const h of hits) {
        let n = h.object;
        while (n && !n.userData?.clickable) n = n.parent;
        if (n && n.userData.clickable) { hoveredNode = n; break; }
      }
      if (hoveredNode) {
        dom.style.cursor = "pointer";
        setHovered({
          name: hoveredNode.userData.title,
          subtitle: hoveredNode.userData.subtitle,
        });
      } else {
        if (!dragging) dom.style.cursor = "grab";
        setHovered(null);
      }

      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };
    animate();
    setReady(true);

    // resize
    const onResize = () => {
      if (!mount) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      running = false;
      window.removeEventListener("resize", onResize);
      dom.removeEventListener("click", onClick);
      dom.removeEventListener("dblclick", onDblClick);
      dom.removeEventListener("mousedown", onDown);
      dom.removeEventListener("mouseup", onUp);
      dom.removeEventListener("mouseleave", onUp);
      dom.removeEventListener("mousemove", onMove);
      dom.removeEventListener("wheel", onWheel);
      dom.removeEventListener("touchstart", onDown);
      dom.removeEventListener("touchend", onUp);
      dom.removeEventListener("touchmove", onMove);
      disposeGroup(worldGroup);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []);

  // Build scene per view
  useEffect(() => {
    const s = stateRef.current;
    if (!s.worldGroup) return;
    disposeGroup(s.worldGroup);
    setSelected(null);
    // Reset follow when navigating levels
    s.followMesh = null;
    setFollowing(null);
    s.target.set(0, 0, 0);

    // Sun light default off, only on for systems. Ambient dims down in system view for real day/night.
    s.sunLight.intensity = 0;
    if (s.ambient) s.ambient.intensity = view.level === "system" ? 0.02 : 0.85;
    // Swap the backdrop texture based on the level we're viewing
    s.viewLevel = view.level;
    if (s.scene) {
      const t = view.level === "universe" ? s.bgTextures?.universe : s.bgTextures?.deep;
      if (t) s.scene.background = t;
    }

    if (view.level === "universe") buildUniverse(s, setView);
    else if (view.level === "galaxy") {
      const gal = UNIVERSE.galaxies.find((g) => g.id === view.galaxyId);
      if (gal) buildGalaxy(s, gal, setView);
    } else if (view.level === "system") {
      const gal = UNIVERSE.galaxies.find((g) => g.id === view.galaxyId);
      const sys = gal?.systems.find((x) => x.id === view.systemId);
      if (sys) buildSystem(s, sys, setSelected);
    }
  }, [view]);

  // Back navigation
  const goBack = () => {
    if (view.level === "system") setView({ level: "galaxy", galaxyId: view.galaxyId, systemId: null, planetIdx: null });
    else if (view.level === "galaxy") setView({ level: "universe", galaxyId: null, systemId: null, planetIdx: null });
    else nav("/");
  };

  const breadcrumb = () => {
    if (view.level === "universe") return "UNIVERSO";
    const gal = UNIVERSE.galaxies.find((g) => g.id === view.galaxyId);
    if (view.level === "galaxy") return `UNIVERSO / ${gal?.id} · ${gal?.name?.toUpperCase()}`;
    const sys = gal?.systems.find((x) => x.id === view.systemId);
    return `UNIVERSO / ${gal?.id} / ${sys?.name?.toUpperCase()}`;
  };

  // Toggle realistic ↔ time-lapse speed
  const toggleSpeed = () => {
    setFastMode((v) => {
      const next = !v;
      if (stateRef.current) stateRef.current.speedMult = next ? 25 : 1;
      return next;
    });
  };

  return (
    <div className="fixed inset-0 bg-black text-white" data-testid="explorer-root">
      <div ref={mountRef} className="absolute inset-0" data-testid="explorer-canvas" />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 p-4 flex items-center justify-between pointer-events-none">
        <div className="flex items-center gap-3 pointer-events-auto">
          <button
            onClick={goBack}
            data-testid="explorer-back"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-primary/40 bg-black/60 backdrop-blur hover:bg-primary/10 font-mono text-[11px] tracking-widest text-primary transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {view.level === "universe" ? "SITE" : t("explorer.back")}
          </button>
          <div className="hidden sm:block font-mono text-[10px] tracking-[0.25em] text-secondary/80">
            {breadcrumb()}
          </div>
        </div>
        <div className="flex items-center gap-2 pointer-events-auto">
          {view.level === "system" && (
            <button
              onClick={toggleSpeed}
              data-testid="explorer-speed-toggle"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm border font-mono text-[10px] tracking-widest transition-colors backdrop-blur ${fastMode ? "border-amber-300/70 bg-amber-500/10 text-amber-200" : "border-primary/40 bg-black/60 text-primary hover:bg-primary/10"}`}
              title={fastMode ? "Voltar pra velocidade realista" : "Acelerar (time-lapse 25×)"}>
              {fastMode ? "⏩ TIME-LAPSE" : "⏱ REALISTA"}
            </button>
          )}
          <button
            onClick={() => setMapOpen(true)}
            data-testid="explorer-map-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-secondary/50 bg-black/60 backdrop-blur hover:bg-secondary/10 font-mono text-[10px] tracking-widest text-secondary transition-colors">
            <MapIcon className="h-3.5 w-3.5" /> {t("explorer.official_map")}
          </button>
          <div className="flex items-center gap-2 px-3 py-2 rounded-sm border border-primary/30 bg-black/60 backdrop-blur">
            <Radio className="h-3.5 w-3.5 text-primary animate-pulse" />
            <span className="font-mono text-[10px] tracking-widest text-primary">SIGNAL LIVE</span>
          </div>
        </div>
      </div>

      {/* Official 2D Universe Map overlay */}
      {mapOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-4"
          data-testid="explorer-map-overlay"
          onClick={() => setMapOpen(false)}>
          <div className="w-full max-w-6xl h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">// REFERÊNCIA OFICIAL</p>
                <h2 className="font-display font-black text-2xl md:text-4xl tracking-tighter text-white">MAPA DO UNIVERSO</h2>
              </div>
              <button
                onClick={() => setMapOpen(false)}
                data-testid="explorer-map-close"
                className="p-2 border border-primary/40 rounded-sm hover:bg-primary/10 text-primary">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto rounded-sm border border-primary/30 bg-black/60">
              <UniverseMapSvg />
            </div>
            <p className="mt-2 font-mono text-[10px] tracking-widest text-muted-foreground text-center">
              {t("explorer.hint_click_galaxy")}
            </p>
          </div>
        </div>
      )}

      {/* Bottom hint */}
      <div className="absolute bottom-4 right-4 z-20 text-right font-mono text-[9px] tracking-widest text-primary/40 pointer-events-none">
        ARRASTAR: ROTACIONAR<br />
        SCROLL: ZOOM<br />
        CLIQUE: EXPLORAR · DUPLO CLIQUE: SEGUIR
      </div>

      {/* Follow chip */}
      {following && (
        <div className="absolute bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-30 pointer-events-auto"
          data-testid="explorer-following-chip">
          <div className="inline-flex items-center gap-3 px-3 py-2 rounded-sm border border-secondary/60 bg-black/80 backdrop-blur font-mono text-[10px] tracking-widest text-secondary shadow-lg shadow-secondary/20">
            <Radio className="h-3.5 w-3.5 animate-pulse" />
            <span>SEGUINDO <span className="text-white font-black">{following.name.toUpperCase()}</span></span>
            <button
              onClick={() => {
                stateRef.current.followMesh = null;
                setFollowing(null);
              }}
              data-testid="explorer-unfollow-btn"
              className="ml-1 p-1 rounded-sm text-secondary/80 hover:text-white hover:bg-secondary/10">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Hover tooltip (floating) */}
      {hovered && (
        <div className="absolute z-30 top-1/2 left-1/2 -translate-x-1/2 mt-16 pointer-events-none">
          <div className="px-3 py-2 rounded-sm border border-primary/50 bg-black/85 backdrop-blur font-mono text-[11px] tracking-widest text-primary">
            <div className="text-primary">{hovered.name}</div>
            {hovered.subtitle && <div className="text-secondary/70 text-[9px] mt-0.5">{hovered.subtitle}</div>}
          </div>
        </div>
      )}

      {/* Selection panel */}
      {selected && (
        <div data-testid="explorer-info" className="absolute bottom-0 left-0 right-0 z-20 border-t border-primary/25 bg-black/90 backdrop-blur">
          <div className="max-w-5xl mx-auto p-5 md:p-6 flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-1">
              <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">{selected.tag}</p>
              <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight mt-1">{selected.name}</h2>
              <p className="mt-3 text-sm text-muted-foreground max-w-2xl leading-relaxed">{selected.desc}</p>
            </div>
            <button
              onClick={() => setSelected(null)}
              data-testid="explorer-close-info"
              className="rounded-sm border border-primary/30 hover:bg-primary/10 p-2 text-primary"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {!ready && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-primary/40 mb-4 animate-pulse">
              <Radio className="h-6 w-6 text-primary" />
            </div>
            <p className="font-mono text-xs tracking-[0.3em] text-primary">CALIBRANDO SINAL...</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Universe view — draws all galaxies as spiral/blob nodes
function buildUniverse(state, setView) {
  const { worldGroup } = state;
  state.target.set(0, 0, 0);
  state.targetDist = 130;
  state.targetRotX = 0.3;
  state.targetRotY = 0;

  UNIVERSE.galaxies.forEach((gal) => {
    const group = new THREE.Group();
    group.position.set(...gal.position);
    group.userData.clickable = true;
    group.userData.title = gal.name;
    group.userData.subtitle = `${gal.id} · ${gal.tag}`;
    group.userData.onClick = () => setView({ level: "galaxy", galaxyId: gal.id, systemId: null, planetIdx: null });

    // spiral galaxy: points + core
    const count = 1800;
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const color = new THREE.Color(gal.color);
    for (let i = 0; i < count; i++) {
      const arm = Math.floor(Math.random() * 3);
      const t = Math.random();
      const r = t * 10 + Math.random() * 0.6;
      const angle = arm * (Math.PI * 2 / 3) + t * 4.5 + (Math.random() - 0.5) * 0.6;
      pos[i * 3] = Math.cos(angle) * r + (Math.random() - 0.5) * 0.5;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 1.4 * (1 - t * 0.7);
      pos[i * 3 + 2] = Math.sin(angle) * r + (Math.random() - 0.5) * 0.5;
      const mix = 0.4 + Math.random() * 0.6;
      col[i * 3] = color.r * mix + (1 - mix);
      col[i * 3 + 1] = color.g * mix + (1 - mix) * 0.9;
      col[i * 3 + 2] = color.b * mix + (1 - mix);
    }
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("color", new THREE.BufferAttribute(col, 3));
    const points = new THREE.Points(g, new THREE.PointsMaterial({
      size: 0.28, vertexColors: true, transparent: true, opacity: 0.9,
      depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true,
    }));
    points.userData.spin = 0.0015;
    group.add(points);

    // core glow
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(1.6, 32, 32),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 })
    );
    group.add(core);
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(2.6, 32, 32),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.14, side: THREE.BackSide, depthWrite: false })
    );
    group.add(halo);

    // invisible clickable sphere
    const hit = new THREE.Mesh(
      new THREE.SphereGeometry(11, 16, 16),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
    );
    group.add(hit);

    // label sprite
    const labelCanvas = document.createElement("canvas");
    labelCanvas.width = 512; labelCanvas.height = 128;
    const ctx = labelCanvas.getContext("2d");
    ctx.font = "600 40px 'JetBrains Mono', monospace";
    ctx.fillStyle = gal.color;
    ctx.fillText(gal.id, 20, 46);
    ctx.font = "700 48px 'Unbounded', sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(gal.name.toUpperCase(), 20, 100);
    const labelTex = new THREE.CanvasTexture(labelCanvas);
    labelTex.colorSpace = THREE.SRGBColorSpace;
    const labelMat = new THREE.SpriteMaterial({ map: labelTex, transparent: true });
    const label = new THREE.Sprite(labelMat);
    label.scale.set(14, 3.5, 1);
    label.position.set(0, 6, 0);
    group.add(label);

    worldGroup.add(group);
  });

  // Natural galaxies (M31, M33, LMC, SMC)
  (UNIVERSE.naturalGalaxies || []).forEach((ng) => {
    const grp = new THREE.Group();
    grp.position.set(...ng.position);
    grp.scale.setScalar(ng.scale);
    grp.userData.clickable = true;
    grp.userData.title = ng.name;
    grp.userData.subtitle = `${ng.id} · ${ng.tag}`;
    grp.userData.onClick = () => {};

    const cnt = 900;
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(cnt * 3);
    const col = new Float32Array(cnt * 3);
    const color = new THREE.Color(ng.color);
    for (let i = 0; i < cnt; i++) {
      const arm = Math.floor(Math.random() * 2);
      const tt = Math.random();
      const rr = tt * 8 + Math.random() * 0.5;
      const angle = arm * Math.PI + tt * 3.5 + (Math.random() - 0.5) * 0.8;
      pos[i * 3] = Math.cos(angle) * rr;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 1.2 * (1 - tt * 0.6);
      pos[i * 3 + 2] = Math.sin(angle) * rr;
      const mix = 0.5 + Math.random() * 0.5;
      col[i * 3] = color.r * mix + (1 - mix);
      col[i * 3 + 1] = color.g * mix + (1 - mix) * 0.9;
      col[i * 3 + 2] = color.b * mix + (1 - mix);
    }
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("color", new THREE.BufferAttribute(col, 3));
    const pts = new THREE.Points(g, new THREE.PointsMaterial({
      size: 0.24, vertexColors: true, transparent: true, opacity: 0.7,
      depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true,
    }));
    pts.userData.spin = 0.001;
    grp.add(pts);

    const core = new THREE.Mesh(
      new THREE.SphereGeometry(1.0, 20, 20),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.75 })
    );
    grp.add(core);
    const hit = new THREE.Mesh(
      new THREE.SphereGeometry(7, 12, 12),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
    );
    grp.add(hit);

    const c = document.createElement("canvas");
    c.width = 384; c.height = 64;
    const ctx = c.getContext("2d");
    ctx.font = "500 22px 'JetBrains Mono', monospace";
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.fillText(ng.name.toUpperCase(), 8, 36);
    const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, opacity: 0.85 }));
    spr.scale.set(8 / ng.scale, 1.4 / ng.scale, 1);
    spr.position.set(0, 5 / ng.scale, 0);
    grp.add(spr);

    worldGroup.add(grp);
  });
}

// ─────────────────────────────────────────────────────────────
function buildGalaxy(state, gal, setView) {
  const { worldGroup } = state;
  state.target.set(0, 0, 0);
  state.targetDist = 55;
  state.targetRotX = 0.35;
  state.targetRotY = 0;

  // faint dust cloud
  const dust = new THREE.Points(
    (() => {
      const cnt = 2500;
      const g = new THREE.BufferGeometry();
      const pos = new Float32Array(cnt * 3);
      const color = new THREE.Color(gal.color);
      for (let i = 0; i < cnt; i++) {
        const arm = Math.floor(Math.random() * 3);
        const t = Math.random();
        const r = t * 40 + Math.random() * 2;
        const a = arm * (Math.PI * 2 / 3) + t * 5 + (Math.random() - 0.5) * 0.5;
        pos[i * 3] = Math.cos(a) * r;
        pos[i * 3 + 1] = (Math.random() - 0.5) * 4 * (1 - t * 0.6);
        pos[i * 3 + 2] = Math.sin(a) * r;
      }
      g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      return g;
    })(),
    new THREE.PointsMaterial({
      size: 0.5, color: gal.color, transparent: true, opacity: 0.35,
      blending: THREE.AdditiveBlending, depthWrite: false,
    })
  );
  dust.userData.spin = 0.0006;
  worldGroup.add(dust);

  // arrange systems in a spiral
  const N = gal.systems.length;
  gal.systems.forEach((sys, i) => {
    const angle = (i / N) * Math.PI * 2 + 0.6;
    const r = 12 + (i % 3) * 6 + Math.random() * 2;
    const x = Math.cos(angle) * r;
    const z = Math.sin(angle) * r;
    const y = (Math.random() - 0.5) * 2;

    const group = new THREE.Group();
    group.position.set(x, y, z);
    group.userData.clickable = true;
    group.userData.title = sys.name;
    group.userData.subtitle = sys.lore?.slice(0, 60) + (sys.lore && sys.lore.length > 60 ? "..." : "");
    group.userData.onClick = () => setView({ level: "system", galaxyId: gal.id, systemId: sys.id, planetIdx: null });

    const sunColor = new THREE.Color(sys.sun?.color || "#ffcc55");
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(0.9, 24, 24),
      new THREE.MeshBasicMaterial({ color: sunColor })
    );
    group.add(core);
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(2.2, 24, 24),
      new THREE.MeshBasicMaterial({ color: sunColor, transparent: true, opacity: 0.25, side: THREE.BackSide, depthWrite: false })
    );
    group.add(halo);
    // hit area
    const hit = new THREE.Mesh(
      new THREE.SphereGeometry(3, 12, 12),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
    );
    group.add(hit);

    // sprite label
    const c = document.createElement("canvas");
    c.width = 384; c.height = 64;
    const ctx = c.getContext("2d");
    ctx.font = "600 28px 'JetBrains Mono', monospace";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(sys.name.toUpperCase(), 8, 40);
    const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true }));
    spr.scale.set(6, 1, 1);
    spr.position.set(0, 2.2, 0);
    group.add(spr);

    worldGroup.add(group);
  });
}

// ─────────────────────────────────────────────────────────────
// System view — sun(s) + orbiting planets
function buildSystem(state, sys, setSelected) {
  const { worldGroup, sunLight } = state;
  state.target.set(0, 0, 0);
  state.targetDist = 45;
  state.targetRotX = 0.28;
  state.targetRotY = 0;

  // Sun(s)
  const sunColor = new THREE.Color(sys.sun?.color || "#ffcc55");
  const sunGeom = new THREE.SphereGeometry(sys.sun?.r || 2.5, 48, 48);

  const makeSunMat = (sun) => {
    if (sun.isSignal) {
      // Núcleo do Sinal — white bright ball
      return new THREE.MeshBasicMaterial({ color: 0xffffff });
    }
    if (sun.isBlackHole) {
      return new THREE.MeshBasicMaterial({ map: sun.tex ? loadTex(sun.tex) : null, color: 0x000000 });
    }
    if (sun.tex) {
      return new THREE.MeshBasicMaterial({ map: loadTex(sun.tex) });
    }
    return new THREE.MeshBasicMaterial({ color: sun.color || 0xffcc55 });
  };

  const sunMesh = new THREE.Mesh(sunGeom, makeSunMat(sys.sun));
  sunMesh.userData.spin = 0.002;
  sunMesh.userData.clickable = true;
  sunMesh.userData.title = sys.sun.name;
  sunMesh.userData.subtitle = sys.name + " · Estrela central";
  sunMesh.userData.onClick = () => setSelected({ name: sys.sun.name, tag: sys.name, desc: sys.lore || "Estrela central do sistema." });
  worldGroup.add(sunMesh);

  // Sun glow / corona — applied to ALL non-signal / non-blackhole suns across galaxies
  if (!sys.sun.isBlackHole) {
    if (!sys.sun.isSignal) {
      const r = sys.sun.r || 2.5;
      const c = sys.sun.color || "#ffcc55";
      // Convert hex→rgb helper for gradient
      const tmp = new THREE.Color(c);
      const R = Math.round(tmp.r * 255);
      const G = Math.round(tmp.g * 255);
      const B = Math.round(tmp.b * 255);
      // Thin bright rim at the silhouette
      const rim = new THREE.Mesh(
        new THREE.SphereGeometry(r * 1.015, 48, 48),
        new THREE.MeshBasicMaterial({
          color: new THREE.Color(Math.min(1, tmp.r + 0.35), Math.min(1, tmp.g + 0.35), Math.min(1, tmp.b + 0.35)),
          transparent: true, opacity: 0.65, side: THREE.BackSide, depthWrite: false, blending: THREE.AdditiveBlending,
        })
      );
      worldGroup.add(rim);
      // Soft wide halo via a billboard sprite with radial gradient in the sun's own color
      const cnv = document.createElement("canvas"); cnv.width = 1024; cnv.height = 1024;
      const ctx = cnv.getContext("2d");
      const g = ctx.createRadialGradient(512, 512, 220, 512, 512, 512);
      g.addColorStop(0.00, `rgba(${Math.min(255, R + 40)},${Math.min(255, G + 40)},${Math.min(255, B + 40)},0.90)`);
      g.addColorStop(0.28, `rgba(${R},${G},${B},0.45)`);
      g.addColorStop(0.55, `rgba(${Math.max(0, R - 20)},${Math.max(0, G - 40)},${Math.max(0, B - 60)},0.15)`);
      g.addColorStop(1.00, `rgba(${R},${G},${B},0)`);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 1024, 1024);
      const haloTex = new THREE.CanvasTexture(cnv);
      haloTex.colorSpace = THREE.SRGBColorSpace;
      const halo = new THREE.Sprite(new THREE.SpriteMaterial({
        map: haloTex, transparent: true, opacity: 0.95,
        depthWrite: false, blending: THREE.AdditiveBlending,
      }));
      halo.scale.setScalar(r * 5.2);
      worldGroup.add(halo);
    } else {
      const glow = new THREE.Mesh(
        new THREE.SphereGeometry((sys.sun?.r || 2.5) * 1.35, 32, 32),
        new THREE.MeshBasicMaterial({ color: sunColor, transparent: true, opacity: 0.22, side: THREE.BackSide, depthWrite: false })
      );
      worldGroup.add(glow);
    }
  } else {
    // Black hole accretion disc
    const disc = new THREE.Mesh(
      new THREE.RingGeometry((sys.sun.r || 3) * 1.4, (sys.sun.r || 3) * 3, 96, 1),
      new THREE.MeshBasicMaterial({ map: loadTex(sys.sun.tex), transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false })
    );
    disc.rotation.x = -Math.PI / 2.2;
    disc.userData.spin = -0.003;
    worldGroup.add(disc);
  }

  // Optional 2nd/3rd suns (binary/ternary systems)
  const orbitSuns = [sys.sun2, sys.sun3].filter(Boolean);
  orbitSuns.forEach((s2, i) => {
    const m = new THREE.Mesh(new THREE.SphereGeometry(s2.r || 1.6, 40, 40), makeSunMat(s2));
    const orbitR = (sys.sun?.r || 2.5) + 4 + i * 3;
    m.userData.orbit = { pivot: new THREE.Vector3(0, 0, 0), radius: orbitR, period: 6 + i * 3, angle0: i * 1.5 };
    m.userData.spin = 0.003;
    m.userData.clickable = true;
    m.userData.title = s2.name;
    m.userData.subtitle = "Estrela secundária";
    m.userData.onClick = () => setSelected({ name: s2.name, tag: sys.name, desc: "Estrela secundária do sistema binário/ternário." });
    worldGroup.add(m);
  });

  // Central sun light
  sunLight.position.set(0, 0, 0);
  sunLight.intensity = sys.sun.isBlackHole ? 0.6 : 6.0;
  sunLight.color = sys.sun.isBlackHole ? new THREE.Color(0xaa77ff) : sunColor;
  sunLight.distance = 500;

    // Orbits + planets (with inclination + tilt variation)
    // Real Sistema Solar: uses pl.au (AU) with sqrt compression so proportions stay real but Neptune fits
    const sunR = sys.sun?.r || 2.5;
    const baseR = sunR + 5;
    const AU_SCALE = 9;              // multiplier for sqrt(AU) → orbit units
    const AU_BASE_OFFSET = sunR + 4; // Mercury sits just outside the sun's glow
    const usesAU = sys.planets.some((p) => typeof p.au === "number");
    // Lookup for moon → parent resolution
    const meshByName = {};
    sys.planets.forEach((pl, idx) => {
      // Skip orbit ring for moons — their orbit follows a moving parent
      const isMoon = !!pl.parent;
      // Realistic orbit radius when `au` provided (planets); moons use moonR from parent center
      const orbitR = isMoon
        ? (pl.moonR || 1.0)
        : (typeof pl.au === "number"
            ? AU_BASE_OFFSET + Math.sqrt(pl.au) * AU_SCALE
            : baseR + idx * 3.2);
      // Orbital plane inclination — near-zero for a flat solar-system disk when using AU
      const incX = isMoon
        ? 0
        : usesAU
          ? (Math.sin(idx * 0.7) * 0.015)                              // ≈0.9° max, ~ecliptic flat
          : (Math.sin(idx * 1.3) * 0.08) + (pl.tilt || 0) * 0.003;
      const incZ = isMoon
        ? 0
        : usesAU
          ? (Math.cos(idx * 0.5) * 0.010)
          : (Math.cos(idx * 0.7) * 0.06);

      // orbit ring — skip for moons (parent moves, so ring would need to follow — complex + visually noisy)
      if (!isMoon) {
        const ringGeom = new THREE.RingGeometry(orbitR - 0.05, orbitR + 0.05, 128);
        const ringMat = new THREE.MeshBasicMaterial({
          color: 0x66aaff,
          transparent: true,
          opacity: 0.28,
          side: THREE.DoubleSide,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
        const ring = new THREE.Mesh(ringGeom, ringMat);
        ring.rotation.x = -Math.PI / 2 + incX;
        ring.rotation.z = incZ;
        worldGroup.add(ring);
      }

      const geo = new THREE.SphereGeometry(pl.r, 48, 48);
      let mat;
      if (pl.tex) {
        const matOpts = { map: loadTex(pl.tex), roughness: 0.9, metalness: 0.02 };
        if (pl.nightMap) {
          // City lights on the dark side — emissiveMap uses map's UVs, modulated by emissive color
          matOpts.emissiveMap = loadTex(pl.nightMap);
          matOpts.emissive = new THREE.Color(0xffffff);
          matOpts.emissiveIntensity = 1.4;
        } else {
          // No self-illumination → only the sunLight lights the sphere → real day/night sides
          matOpts.emissive = new THREE.Color(0x000000);
          matOpts.emissiveIntensity = 0;
        }
        if (pl.normalMap) {
          matOpts.normalMap = loadTex(pl.normalMap);
          matOpts.normalScale = new THREE.Vector2(0.8, 0.8);
        }
        mat = new THREE.MeshStandardMaterial(matOpts);
      } else {
        mat = new THREE.MeshStandardMaterial({ color: 0x3388ff, roughness: 0.9, metalness: 0.02 });
      }
      const mesh = new THREE.Mesh(geo, mat);
      mesh.rotation.z = (pl.tilt || 0) * (Math.PI / 180);
      mesh.userData.spin = 0.0018;
      mesh.userData.orbit = {
        // For moons, pivot is set dynamically from parentMesh each frame; for planets it's fixed at origin
        pivot: new THREE.Vector3(0, 0, 0),
        radius: orbitR,
        period: pl.period,
        angle0: idx * 0.9,
        incX,
        incZ,
        parentMesh: isMoon ? (meshByName[pl.parent] || null) : null,
      };
      mesh.userData.clickable = true;
      mesh.userData.title = pl.name;
      mesh.userData.subtitle = pl.desc?.slice(0, 60) || sys.name;
      mesh.userData.onClick = () => setSelected({
        name: pl.name,
        tag: isMoon ? `${sys.name} · Lua de ${pl.parent}` : `${sys.name} · Planeta ${idx + 1}`,
        desc: pl.desc || `${pl.name} é um dos mundos do sistema ${sys.name}. Órbita: ${pl.period.toFixed(2)} ciclos. Raio relativo: ${pl.r.toFixed(2)}.`,
      });

    // atmosphere shell — lit by sunLight so it only glows on the day-side rim (dark side stays dark)
    if (pl.atmo) {
      const atmoMesh = new THREE.Mesh(
        new THREE.SphereGeometry(pl.r * 1.06, 32, 32),
        new THREE.MeshStandardMaterial({
          color: pl.atmo,
          transparent: true,
          opacity: 0.35,
          side: THREE.BackSide,
          roughness: 1.0,
          metalness: 0.0,
          depthWrite: false,
        })
      );
      mesh.add(atmoMesh);
    }

    // clouds shell — separately spinning child (uses grayscale as alpha)
    if (pl.clouds) {
      const cloudsTex = loadTex(pl.clouds);
      const cloudsMesh = new THREE.Mesh(
        new THREE.SphereGeometry(pl.r * 1.02, 48, 48),
        new THREE.MeshStandardMaterial({
          map: cloudsTex,
          alphaMap: cloudsTex,
          transparent: true,
          opacity: 0.85,
          depthWrite: false,
          roughness: 1.0,
          metalness: 0.0,
        })
      );
      cloudsMesh.userData.spin = pl.cloudsSpeed || 0.0022;
      mesh.add(cloudsMesh);
    }

    // ring
    if (pl.ring) {
      const inner = pl.r * (pl.ringInner || 1.4);
      const outer = pl.r * (pl.ringOuter || 2.2);
      const ringGeom = new THREE.RingGeometry(inner, outer, 128, 4);
      // Remap UVs so the ring texture maps radially (x = radial position from inner→outer)
      const pos = ringGeom.attributes.position;
      const uv = ringGeom.attributes.uv;
      const v = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i);
        const rad = Math.sqrt(v.x * v.x + v.y * v.y);
        const u = THREE.MathUtils.clamp((rad - inner) / (outer - inner), 0, 1);
        uv.setXY(i, u, 0.5);
      }
      uv.needsUpdate = true;
      const ringMesh = new THREE.Mesh(
        ringGeom,
        new THREE.MeshBasicMaterial({
          map: loadTex(pl.ring),
          transparent: true,
          opacity: 1.0,
          side: THREE.DoubleSide,
          depthWrite: false,
          alphaTest: 0.02,
        })
      );
      const rt = (pl.ringTilt ?? 27) * (Math.PI / 180);
      ringMesh.rotation.x = -Math.PI / 2 + rt;
      mesh.add(ringMesh);
    }

    worldGroup.add(mesh);
    meshByName[pl.name] = mesh;
  });

  // Asteroid belt — Sistema Solar only: ring of point-sprites between Mars (1.52 AU) and Jupiter (5.20 AU)
  if (usesAU) {
    const beltInner = AU_BASE_OFFSET + Math.sqrt(2.2) * AU_SCALE;
    const beltOuter = AU_BASE_OFFSET + Math.sqrt(3.3) * AU_SCALE;
    const beltCount = 900;
    const beltPos = new Float32Array(beltCount * 3);
    for (let i = 0; i < beltCount; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = beltInner + Math.random() * (beltOuter - beltInner);
      const jitterY = (Math.random() - 0.5) * 0.4;
      beltPos[i * 3    ] = Math.cos(a) * r;
      beltPos[i * 3 + 1] = jitterY;
      beltPos[i * 3 + 2] = Math.sin(a) * r;
    }
    const beltGeom = new THREE.BufferGeometry();
    beltGeom.setAttribute("position", new THREE.BufferAttribute(beltPos, 3));
    const beltMat = new THREE.PointsMaterial({
      size: 0.10,
      color: 0xaa9880,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
    });
    const belt = new THREE.Points(beltGeom, beltMat);
    belt.userData.spin = 0.0004;
    worldGroup.add(belt);
  }
}
